import { useCallback, useEffect, useRef, useState } from 'react'

import { MOBILE_BREAKPOINT_QUERY } from './adPlacements.js'

const DEFAULT_ACTIVITY_WINDOW_MS = 120 * 1000

export const useMobileViewport = () => {
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => (
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches
        : false
    ),
  )

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined

    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY)
    const syncViewportMode = () => setIsMobileViewport(mediaQuery.matches)
    syncViewportMode()

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncViewportMode)
      return () => mediaQuery.removeEventListener('change', syncViewportMode)
    }

    mediaQuery.addListener(syncViewportMode)
    return () => mediaQuery.removeListener(syncViewportMode)
  }, [])

  return isMobileViewport
}

const useRecentUserActivity = (activityWindowMs = DEFAULT_ACTIVITY_WINDOW_MS) => {
  const lastActivityAtRef = useRef(Date.now())

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const markActivity = () => {
      lastActivityAtRef.current = Date.now()
    }
    const options = { passive: true }
    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart']
    events.forEach(eventName => window.addEventListener(eventName, markActivity, options))

    return () => {
      events.forEach(eventName => window.removeEventListener(eventName, markActivity, options))
    }
  }, [])

  return useCallback(
    () => Date.now() - lastActivityAtRef.current <= activityWindowMs,
    [activityWindowMs],
  )
}

export const useAdsterraLoadCycle = ({
  containerRef,
  disabled = false,
  placementConfig,
  resetKey,
}) => {
  const isUserRecentlyActive = useRecentUserActivity(placementConfig?.activityWindowMs)
  const hasQueuedInitialLoadRef = useRef(false)
  const lastVisibilityRetryAtRef = useRef(0)
  const wasViewableRef = useRef(false)
  const [canLoad, setCanLoad] = useState(false)
  const [isViewable, setIsViewable] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)

  useEffect(() => {
    hasQueuedInitialLoadRef.current = false
    lastVisibilityRetryAtRef.current = 0
    wasViewableRef.current = false
    setCanLoad(false)
    setIsViewable(false)
    setLoadAttempt(0)
  }, [disabled, resetKey])

  useEffect(() => {
    if (disabled || !placementConfig || typeof window === 'undefined') return undefined

    const container = containerRef.current
    if (!container) return undefined

    let isCancelled = false
    let delayId = null
    let observer = null
    const initialDelayMs = placementConfig.initialDelayMs ?? 2500
    const visibilityRetryMs = Number(placementConfig.visibilityRetryMs || 0)

    const runQueuedLoad = () => {
      if (isCancelled) return

      if (hasQueuedInitialLoadRef.current) {
        const now = Date.now()
        if (visibilityRetryMs && now - lastVisibilityRetryAtRef.current < visibilityRetryMs) return
        lastVisibilityRetryAtRef.current = now
        setLoadAttempt(current => current + 1)
        return
      }

      hasQueuedInitialLoadRef.current = true
      lastVisibilityRetryAtRef.current = Date.now()
      setCanLoad(true)
    }

    const queueLoad = () => {
      if (delayId) window.clearTimeout(delayId)
      if (initialDelayMs <= 0) {
        runQueuedLoad()
        return
      }
      delayId = window.setTimeout(runQueuedLoad, initialDelayMs)
    }

    if (placementConfig.lazy && typeof window.IntersectionObserver === 'function') {
      observer = new window.IntersectionObserver((entries) => {
        const visible = entries.some(entry => entry.isIntersecting || entry.intersectionRatio > 0)
        setIsViewable(visible)
        if (visible && !wasViewableRef.current) queueLoad()
        wasViewableRef.current = visible
      }, { rootMargin: '220px 0px', threshold: 0.01 })
      observer.observe(container)
    } else if (typeof document !== 'undefined' && document.readyState === 'complete') {
      wasViewableRef.current = true
      setIsViewable(true)
      queueLoad()
    } else {
      const onLoad = () => {
        wasViewableRef.current = true
        setIsViewable(true)
        queueLoad()
      }
      window.addEventListener('load', onLoad, { once: true })
      return () => {
        isCancelled = true
        if (delayId) window.clearTimeout(delayId)
        window.removeEventListener('load', onLoad)
      }
    }

    return () => {
      isCancelled = true
      if (delayId) window.clearTimeout(delayId)
      observer?.disconnect()
    }
  }, [containerRef, disabled, placementConfig, resetKey])

  useEffect(() => {
    if (disabled || !canLoad || !placementConfig || typeof window === 'undefined') return undefined
    const refreshMs = Number(placementConfig.refreshMs || 0)
    if (!refreshMs) return undefined

    const intervalId = window.setInterval(() => {
      const documentIsHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden'
      if (documentIsHidden || !isViewable) return
      if (placementConfig.requireRecentActivity && !isUserRecentlyActive()) return
      setLoadAttempt(current => current + 1)
    }, refreshMs)

    return () => window.clearInterval(intervalId)
  }, [canLoad, disabled, isUserRecentlyActive, isViewable, placementConfig])

  return {
    canLoad,
    isViewable,
    loadAttempt,
  }
}
